import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProductTierGrid from '../components/ProductTierGrid';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import PaymentForm from '../components/PaymentForm';
import PlanChangeModal from '../components/PlanChangeModal';
import CancellationModal from '../components/CancellationModal';
import '../styles/AccountPage.css';

interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

interface UserSubscription {
  subscription_id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  status: string;
  current_period_end: string;
}

const AccountPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [productTiers, setProductTiers] = useState<ProductTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<ProductTier | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [targetTier, setTargetTier] = useState<ProductTier | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect will be handled by parent component or router
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch product tiers
        const response = await fetch('/api/product-tiers', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch product tiers: ${response.status}`);
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to load product tiers');
        }

        setProductTiers(data.tiers || []);

        // TEMPORARY: Add mock tiers for testing if none exist
        if (!data.tiers || data.tiers.length === 0) {
          const mockTiers: ProductTier[] = [
            {
              id: 'basic_plan_id',
              name: 'Basic Plan',
              description: 'Basic features for small teams',
              price_id: 'price_basic',
              amount: 999, // $9.99
              currency: 'usd',
              interval: 'month',
              features: ['Feature 1', 'Feature 2', 'Basic Support'],
              metadata: {}
            },
            {
              id: 'premium_plan_id',
              name: 'Premium Plan',
              description: 'Advanced features for growing teams',
              price_id: 'price_premium',
              amount: 1999, // $19.99
              currency: 'usd',
              interval: 'month',
              features: ['All Basic Features', 'Feature 3', 'Feature 4', 'Priority Support'],
              metadata: { popular: 'true' }
            },
            {
              id: 'enterprise_plan_id',
              name: 'Enterprise Plan',
              description: 'Full features for large organizations',
              price_id: 'price_enterprise',
              amount: 4999, // $49.99
              currency: 'usd',
              interval: 'month',
              features: ['All Premium Features', 'Enterprise Features', 'Dedicated Support'],
              metadata: {}
            }
          ];
          setProductTiers(mockTiers);
        }

        // Fetch current user subscription
        const subscriptionResponse = await fetch('/api/user-subscription', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          if (subscriptionData.success) {
            setCurrentSubscription(subscriptionData.subscription);
          }
        }

        // TEMPORARY: Add mock data for testing upgrade button functionality
        if (!currentSubscription) {
          const mockSubscription = {
            subscription_id: 'test_sub_123',
            stripe_product_id: 'basic_plan_id', // This should match one of the tier IDs
            stripe_price_id: 'test_price_basic',
            status: 'active',
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          };
          setCurrentSubscription(mockSubscription);
        }

      } catch (err) {
        console.error('Error fetching account data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load account information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  const handlePlanSelect = async (tier: ProductTier) => {
    try {
      console.log('Plan selected:', tier);
      setSelectedTier(tier);
      setShowPaymentForm(true);
    } catch (err) {
      console.error('Error selecting plan:', err);
      setError('Failed to select plan. Please try again.');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedTier(null);
    setError(null);
    
    // Refresh the page data to show updated subscription
    window.location.reload();
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setSelectedTier(null);
  };

  const handlePlanChange = (tier: ProductTier) => {
    setTargetTier(tier);
    setShowPlanChangeModal(true);
  };

  const handleCancellation = () => {
    setShowCancellationModal(true);
  };

  const handlePlanChangeComplete = () => {
    setShowPlanChangeModal(false);
    setTargetTier(null);
    setError(null);
    
    // Refresh the page data to show updated subscription
    window.location.reload();
  };

  const handleCancellationComplete = () => {
    setShowCancellationModal(false);
    setError(null);
    
    // Refresh the page data to show updated subscription
    window.location.reload();
  };

  const handleModalCancel = () => {
    setShowPlanChangeModal(false);
    setShowCancellationModal(false);
    setTargetTier(null);
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
  };

  if (!isAuthenticated) {
    return (
      <div className="account-page">
        <div className="account-container">
          <div className="account-message">
            <h2>Please log in to access your account</h2>
            <p>You need to be logged in to view and manage your subscription plans.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="account-page">
        <div className="account-container">
          <LoadingSpinner message="Loading your account information..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-page">
        <div className="account-container">
          <ErrorMessage 
            message={error} 
            onRetry={() => window.location.reload()} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">
        <div className="account-header">
          <h1>Your Account</h1>
          <div className="user-info">
            <p>Welcome back, {user?.first_name} {user?.last_name}!</p>
            <p className="email">{user?.email}</p>
          </div>
        </div>

        <div className="subscription-section">
          <h2>Subscription Plans</h2>
          {!currentSubscription && (
            <div className="subscription-prompt">
              <p>Choose a plan to get started with Pouncer and begin tracking phone numbers!</p>
            </div>
          )}
          
          {currentSubscription && (
            <div className="current-subscription">
              <h3>Current Plan</h3>
              <div className="subscription-details">
                <p><strong>Status:</strong> {currentSubscription.status}</p>
                <p><strong>Next billing:</strong> {new Date(currentSubscription.current_period_end).toLocaleDateString()}</p>
              </div>
            </div>
          )}

          <ProductTierGrid
            tiers={productTiers}
            currentSubscription={currentSubscription}
            onSelectPlan={handlePlanSelect}
            onPlanChange={handlePlanChange}
            onCancellation={handleCancellation}
          />
        </div>

        {showPaymentForm && selectedTier && (
          <PaymentForm
            tier={selectedTier}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
            onError={handlePaymentError}
          />
        )}

        {showPlanChangeModal && targetTier && currentSubscription && (
          <PlanChangeModal
            currentTier={productTiers.find(t => t.id === currentSubscription.stripe_product_id) || null}
            newTier={targetTier}
            currentSubscription={currentSubscription}
            onConfirm={handlePlanChangeComplete}
            onCancel={handleModalCancel}
            onError={handlePaymentError}
          />
        )}

        {showCancellationModal && currentSubscription && (
          <CancellationModal
            currentTier={productTiers.find(t => t.id === currentSubscription.stripe_product_id) || null}
            currentSubscription={currentSubscription}
            onConfirm={handleCancellationComplete}
            onCancel={handleModalCancel}
            onError={handlePaymentError}
          />
        )}
      </div>
    </div>
  );
};

export default AccountPage;
